// ============================================================================
// Calendar helpers (Epic 11) — pure, framework-free logic for the content
// calendar. Kept side-effect-free (no `api` import) so the day/week/month math,
// status cycling and overdue derivation are unit-testable in isolation.
//
// Dates are handled as ISO `yyyy-MM-dd` strings end-to-end (the API contract).
// Because that format is zero-padded, lexicographic string comparison is a valid
// date comparison — which keeps `isOverdue` allocation-free and timezone-proof.
// ============================================================================
import {
  format,
  parseISO,
  addDays,
  addMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth as dfIsSameMonth,
} from "date-fns";

// pt-BR month/weekday names kept local on purpose: date-fns/locale ships an ESM
// barrel that Jest (CRA transformIgnorePatterns) won't transpile, and these few
// tables are cheaper and more test-stable than pulling the locale in.
const WEEKDAYS_SHORT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const WEEKDAYS_LONG = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const MONTHS_LONG = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];
const MONTHS_SHORT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

// Weeks start on Sunday (BR calendar convention).
const WEEK_STARTS_ON = 0;

// The three persisted statuses. `overdue` is NOT a status — it is derived on the
// client from a `planned` entry whose date is already in the past (Epic 11.1).
export const STATUS_ORDER = ["planned", "posted", "not_posted"];

export function todayISO() {
  return format(new Date(), "yyyy-MM-dd");
}

// Tapping the status chip cycles planned → posted → not_posted → planned.
export function nextStatus(status) {
  const i = STATUS_ORDER.indexOf(status);
  if (i === -1) return "posted";
  return STATUS_ORDER[(i + 1) % STATUS_ORDER.length];
}

// Derived overdue flag: a planned entry whose date is strictly before today.
export function isOverdue(entry, today = todayISO()) {
  if (!entry || entry.status !== "planned" || !entry.entry_date) return false;
  return today > entry.entry_date;
}

// Chip presentation for a given entry (overdue takes precedence over planned).
export function statusMeta(entry, today = todayISO()) {
  if (isOverdue(entry, today)) {
    return { key: "overdue", label: "Atrasado", className: "bg-[#E5484D]/15 text-[#E5484D]" };
  }
  switch (entry?.status) {
    case "posted":
      return { key: "posted", label: "Postado", className: "bg-brand-sage/15 text-brand-sage" };
    case "not_posted":
      return { key: "not_posted", label: "Não postado", className: "bg-brand-elevated text-brand-ink2" };
    case "planned":
    default:
      return { key: "planned", label: "Planejado", className: "bg-brand-rose/15 text-brand-rose" };
  }
}

function sortEntries(a, b) {
  const ta = a.entry_time || "";
  const tb = b.entry_time || "";
  // Timed entries come first in chronological order; untimed ("anytime") entries
  // fall to the bottom, sorted by title.
  if (ta && tb) return ta < tb ? -1 : ta > tb ? 1 : 0;
  if (ta) return -1;
  if (tb) return 1;
  return (a.title || "").localeCompare(b.title || "");
}

// Group entries by their ISO day, each bucket sorted by time then title.
export function groupByDay(entries = []) {
  const map = {};
  for (const e of entries) {
    if (!e || !e.entry_date) continue;
    (map[e.entry_date] ||= []).push(e);
  }
  for (const k of Object.keys(map)) map[k].sort(sortEntries);
  return map;
}

// ── Grid math ───────────────────────────────────────────────────────────────

// Full month grid (leading/trailing days from adjacent months) as weeks of 7.
export function monthMatrix(cursorISO) {
  const base = parseISO(cursorISO);
  const gridStart = startOfWeek(startOfMonth(base), { weekStartsOn: WEEK_STARTS_ON });
  const gridEnd = endOfWeek(endOfMonth(base), { weekStartsOn: WEEK_STARTS_ON });
  const days = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(format(d, "yyyy-MM-dd"));
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

// The 7 days of the calendar week containing `cursorISO` (Sunday → Saturday).
export function weekDays(cursorISO) {
  const start = startOfWeek(parseISO(cursorISO), { weekStartsOn: WEEK_STARTS_ON });
  return Array.from({ length: 7 }, (_, i) => format(addDays(start, i), "yyyy-MM-dd"));
}

// `count` consecutive days starting at `startISO` (used by Minha Semana: today +6).
export function daysFrom(startISO, count = 7) {
  const base = parseISO(startISO);
  return Array.from({ length: count }, (_, i) => format(addDays(base, i), "yyyy-MM-dd"));
}

// Move the cursor by `amount` of `unit` ("day" | "week" | "month").
export function shiftISO(cursorISO, amount, unit) {
  const base = parseISO(cursorISO);
  if (unit === "month") return format(addMonths(base, amount), "yyyy-MM-dd");
  const days = unit === "week" ? amount * 7 : amount;
  return format(addDays(base, days), "yyyy-MM-dd");
}

export function isToday(iso, today = todayISO()) {
  return iso === today;
}

export function isSameMonthISO(iso, refISO) {
  return dfIsSameMonth(parseISO(iso), parseISO(refISO));
}

// ── pt-BR formatting ──────────────────────────────────────────────────────────

function cap(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function fmtWeekdayShort(iso) {
  return WEEKDAYS_SHORT[parseISO(iso).getDay()];
}

export function fmtDayNum(iso) {
  return String(parseISO(iso).getDate());
}

export function fmtMonthYear(iso) {
  const d = parseISO(iso);
  return `${cap(MONTHS_LONG[d.getMonth()])} de ${d.getFullYear()}`;
}

export function fmtFullDate(iso) {
  const d = parseISO(iso);
  return `${cap(WEEKDAYS_LONG[d.getDay()])}, ${d.getDate()} de ${MONTHS_LONG[d.getMonth()]}`;
}

// hh:mm from a `HH:mm[:ss]` time string; "" when absent.
export function fmtTime(t) {
  return t ? t.slice(0, 5) : "";
}

// Compact week label, e.g. "6–12 de jul" or "29 de jun – 5 de jul".
export function fmtWeekRange(fromISO, toISO) {
  const a = parseISO(fromISO);
  const b = parseISO(toISO);
  if (dfIsSameMonth(a, b)) {
    return `${a.getDate()}–${b.getDate()} de ${MONTHS_SHORT[b.getMonth()]}`;
  }
  return `${a.getDate()} de ${MONTHS_SHORT[a.getMonth()]} – ${b.getDate()} de ${MONTHS_SHORT[b.getMonth()]}`;
}
